import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Der Nachher-Schnitt GENAU der Produkte eines Optimierungslaufs.
 *
 * WARUM NICHT `optimization_jobs.product_ids`
 *
 * Welle 3 hat den Erfolgs-Ring und Welle 12 die Desktop-Meldung auf diese
 * Spalte umgebaut — und beide messen seitdem nichts. Die Spalte wird von
 * BEIDEN Erzeugern leer geschrieben: `enqueue-optimization-batches/index.ts`
 * (`product_ids: []`, Zeile 578) und `batch-opt-collector/index.ts`
 * (`product_ids: []`, Zeile 299). In Produktion sind 129 von 129 Jobs seit
 * 2026-05 mit leerem Array gespeichert (267 von 287 ueber die ganze Historie;
 * die 20 gefuellten sind alle aus 04/2026, vor dem Umbau). Die Edge Function
 * `check-job-failures/stale-job-handlers.ts` traegt denselben Befund als
 * Kommentar: "product_ids auf dem Job-Record waere der praezisere Scope, ist
 * aber leer".
 *
 * Was der Nutzer davon sah: der Ring stand dauerhaft auf "—" mit dem Hinweis
 * "Score wird berechnet …" (`runProductIds.length === 0` schaltete die Abfrage
 * per `enabled` aus, es wurde also nie eine gestellt), und die Meldung fiel
 * immer auf `bodyNoScore` zurueck. Eine Praedikat-Form kann richtig sein und
 * die Groesse trotzdem nie ankommen — deshalb prueft der Lauf-Sweep seit
 * diesem Durchgang auch die VERFUEGBARKEIT der Quelle
 * (`emptyJobColumnReads` in `src/test/runScope.ts`).
 *
 * DIE QUELLE, DIE ES GIBT: `optimization_events` traegt `job_id` auf jeder
 * Zeile (Index `idx_optimization_events_job_id`), eine Zeile pro optimiertem
 * Produkt dieses Laufs. Dieselbe Quelle nutzen die Beispiel-Auswahl des
 * Erfolgsbilds, `Phase2Texte` und — mit genau derselben Seitenlogik —
 * `job-lifecycle.ts:208-218` fuer den Auto-Upload.
 *
 * Die saubere Loesung waere eine RPC `avg_run_post_score(p_job_id)` analog
 * `avg_latest_total_score`: ein Request, keine Kappung, kein Chunking. Sie
 * fehlt, und `avg_latest_total_score` ist fuer `authenticated` NICHT
 * ausfuehrbar (nur `service_role`), also ist der zweistufige Weg von hier aus
 * der einzige. Solange das so ist, gilt die Obergrenze unten.
 */

/** PostgREST liefert ohne `range` maximal 1000 Zeilen. */
export const RUN_EVENT_PAGE_SIZE = 1000;

/**
 * Produkt-IDs je Score-Request. 200 UUIDs sind rund 7,4 kB in der URL — mehr
 * reisst die ueblichen 8-kB-Header-Grenzen.
 */
export const RUN_SCORE_CHUNK_SIZE = 200;

/**
 * Wie viele Seiten der Ereignis-Tabelle wir fuer EINEN Lauf lesen.
 *
 * Zusammen mit `RUN_EVENT_PAGE_SIZE` die Obergrenze der Laufgroesse, die von
 * der Oberflaeche aus gemittelt werden kann: 10 Requests fuer die IDs, danach
 * bis zu 50 fuer die Scores. Darueber wird NICHT gemittelt und NICHT
 * geschaetzt, sondern gesagt (`success.scoreRunTooLarge`) — der Schnitt einer
 * unbestimmten Teilmenge sieht genauso aus wie ein richtiger.
 *
 * Die Grenze deckt 55 der 58 manuellen Laeufe seit 2026-05 (die drei darueber:
 * 81.479, 71.346, 38.597 Produkte). Sie verschwindet, sobald es die RPC gibt.
 */
export const RUN_EVENT_MAX_PAGES = 10;

/** Groesster Lauf, den diese Datei mitteln kann. */
export const RUN_MEASURABLE_MAX_PRODUCTS = RUN_EVENT_PAGE_SIZE * RUN_EVENT_MAX_PAGES;

export type RunProductIds =
  /** Vollstaendige Produktmenge dieses Laufs. */
  | { readonly kind: 'ids'; readonly ids: readonly string[] }
  /** Groesser als `RUN_MEASURABLE_MAX_PRODUCTS` — hier wird nicht gemittelt. */
  | { readonly kind: 'too-large' }
  /** Mindestens eine Seite hat einen Fehler geliefert. */
  | { readonly kind: 'unavailable' };

function report(err: unknown, action: string): void {
  Sentry.captureException(err, { tags: { feature: 'optimization-success', action } });
}

/**
 * Die Produkte DIESES Laufs, aus `optimization_events`.
 *
 * Seitenweise wie `job-lifecycle.ts`: nach `product_id` sortiert (stabile
 * Reihenfolge ueber die Seiten hinweg), Abbruch bei einer unvollen Seite.
 * `Set`, weil ein Produkt mehrere Ereignisse desselben Laufs haben kann — die
 * stillen Retries von `checkAndCompleteJob` schreiben pro Versuch eine Zeile,
 * und ein Produkt zweimal im Schnitt waere eine Gewichtung, die niemand
 * beschlossen hat.
 *
 * Ein Fehler auf EINER Seite macht die ganze Menge unbrauchbar: die uebrigen
 * Seiten sind dann eine unbestimmte Teilmenge des Laufs, und ihr Schnitt ist
 * von einem richtigen nicht zu unterscheiden.
 */
export async function fetchRunProductIds(jobId: string): Promise<RunProductIds> {
  const ids = new Set<string>();
  for (let page = 0; page < RUN_EVENT_MAX_PAGES; page++) {
    const from = page * RUN_EVENT_PAGE_SIZE;
    const { data, error } = await supabase
      .from('optimization_events')
      .select('product_id')
      .eq('job_id', jobId)
      .order('product_id')
      .range(from, from + RUN_EVENT_PAGE_SIZE - 1);
    if (error) {
      report(error, 'run-product-ids');
      return { kind: 'unavailable' };
    }
    const rows = data ?? [];
    for (const row of rows) {
      if (row.product_id) ids.add(row.product_id);
    }
    if (rows.length < RUN_EVENT_PAGE_SIZE) return { kind: 'ids', ids: [...ids] };
  }
  // Zehnte Seite noch voll: der Lauf ist groesser als das, was von hier aus
  // ehrlich gemittelt werden kann.
  return { kind: 'too-large' };
}

/**
 * Nachher-Schnitt ueber die uebergebenen Produkte.
 *
 * `null` heisst "kein belastbarer Wert" und NICHT "0". Drei Gruende, alle
 * muessen zu `null` fuehren:
 *
 *   1. Noch keine Zeile geschrieben — der Score-Job laeuft nach dem
 *      Optimierungslauf und hinkt hinterher.
 *   2. Mindestens ein Block hat einen FEHLER geliefert. `r.data ?? []` verwarf
 *      den still, und der Schnitt der uebrigen Bloecke sieht aus wie der
 *      Schnitt des Laufs.
 *   3. Es gibt nur VERALTETE Zeilen (`calculated_at` vor `notBefore`) — siehe
 *      Frische-Praedikat unten.
 *
 * FRISCHE-PRAEDIKAT (`notBefore`, Welle 13): gezaehlt werden nur Zeilen mit
 * `calculated_at >= notBefore`; die Aufrufer reichen `completed_at` des
 * Optimierungs-Jobs durch. Ohne das Praedikat mass diese Funktion die
 * richtigen PRODUKTE, aber nicht zwingend den richtigen ZEITPUNKT:
 * post-opt-Score-Jobs laufen feed-weit, jedes Produkt eines je gescorten Feeds
 * traegt also eine `post_optimization`-Zeile — vom LETZTEN Lauf. Drei reale
 * Fenster, in denen das Erfolgsbild vor den frischen Scores rendert und der
 * erste nicht-null-Wert der ALTE waere:
 *   (a) das Score-Trigger-Rate-Limit (3 Calls / 5 min, geteilt) trifft
 *       `finalizeJob` mit 429 — es entsteht gar kein Score-Job;
 *   (b) die Live-Job-Suppression unterdrueckt den Enqueue des zweiten Laufs,
 *       waehrend der feed-weite Score-Job des ersten noch laeuft;
 *   (c) der Score-Job endet `failed`, das Modal schaltet sofort auf Erfolg.
 * In allen drei Faellen zeigte der Ring frueher die alten Scores derselben
 * Produkte als Ertrag DIESES Laufs (z. B. "Unveraendert vs. vorher") und fror
 * ein. Stale-only ergibt jetzt `null`, der Platzhalter bleibt ehrlich stehen.
 * `>=` statt `>`, weil beide Zeitstempel serverseitig entstehen und der
 * Score-Write fruehestens im selben Moment wie der Job-Abschluss landet.
 *
 * Die Backend-Luecken (a) und (b) selbst sind hier NICHT behoben — supabase/**
 * ist ein anderer Aenderungspfad; diese Funktion hoert nur auf zu luegen.
 *
 * Kein `feed_id`-Praedikat: `product_scores` ist ueber
 * UNIQUE (product_id, score_type) indiziert, die Produkt-IDs sind UUIDs, und
 * die RLS-Join-Kette scoped ohnehin auf den Besitzer. Ein zusaetzliches
 * Praedikat auf den Feed wuerde nichts verengen, was die ID-Liste nicht schon
 * verengt.
 */
export async function avgPostScore(
  productIds: readonly string[],
  notBefore: string,
): Promise<number | null> {
  if (productIds.length === 0) return null;
  const chunks: string[][] = [];
  for (let i = 0; i < productIds.length; i += RUN_SCORE_CHUNK_SIZE) {
    chunks.push([...productIds.slice(i, i + RUN_SCORE_CHUNK_SIZE)]);
  }
  const results = await Promise.all(
    chunks.map((chunk) =>
      supabase
        .from('product_scores')
        .select('total_score')
        .eq('score_type', 'post_optimization')
        .gte('calculated_at', notBefore)
        .not('total_score', 'is', null)
        .in('product_id', chunk),
    ),
  );
  const failed = results.find((r) => r.error);
  if (failed) {
    report(failed.error, 'run-score-chunk');
    return null;
  }
  const values = results
    .flatMap((r) => r.data ?? [])
    .map((r) => Number(r.total_score))
    .filter((v) => Number.isFinite(v));
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Beides in einem Schritt, fuer Aufrufer ohne eigenen Zwischenzustand.
 *
 * `too-large` und `unavailable` werden hier zu `null`: eine Desktop-Meldung
 * kann keine Obergrenze erklaeren, sie laesst den Score-Teil dann weg
 * (`bodyNoScore`). `notBefore` ist `completed_at` des Optimierungs-Jobs
 * (Frische-Praedikat, siehe `avgPostScore`).
 */
export async function avgRunPostScore(jobId: string, notBefore: string): Promise<number | null> {
  const run = await fetchRunProductIds(jobId);
  if (run.kind !== 'ids') return null;
  return avgPostScore(run.ids, notBefore);
}
